import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import './BulkGenerateModal.css';

// Mirrors admin-website-backend/routes/nabers-linkgen.js — kept in sync so the
// preview shows exactly what the server will do. '#' resolves to Count (the house
// convention) and 'tonne' has no quantity type, so those rows are excluded.
const UNIT_RULES = {
    'm³': { quantityTypeName: 'CubicMeters', weightField: 'density_kg_m3' },
    'm²': { quantityTypeName: 'SquareMeters', weightField: 'area_density_kg_m2' },
    'm': { quantityTypeName: 'Meters', weightField: 'mass_per_m_kg_m' },
    '#': { quantityTypeName: 'Count', weightField: 'mass_per_unit_kg_unit' },
    'kg': { quantityTypeName: 'kg', weightField: null },
};

const normaliseUnit = (raw) => {
    if (raw === null || raw === undefined) return '';
    const u = String(raw).trim();
    if (!u || u.toUpperCase() === 'NULL') return '';
    if (u.toLowerCase() === 'm3') return 'm³';
    if (u.toLowerCase() === 'm2') return 'm²';
    return u;
};

const numOrNull = (v) => {
    if (v === null || v === undefined || v === '') return null;
    const n = parseFloat(v);
    return Number.isNaN(n) ? null : n;
};

// The MUR's Co2 is the record's GWP-fossil (biogenic has its own MUR); a record with no fossil figure
// falls back to GWP-total, flagged — same rule as the server (nabers-linkgen planChainForEf).
const co2For = (row) => {
    const fossil = numOrNull(row.gwp_fossil_kg_co2e_quant);
    if (fossil !== null) return { co2: fossil, co2FromTotal: false };
    const total = numOrNull(row.gwp_total_kg_co2e_quant);
    return { co2: total, co2FromTotal: total !== null };
};

export const planRow = (row, includeZeroWeight) => {
    const unit = normaliseUnit(row.unit);
    const rule = UNIT_RULES[unit];
    // the records table sends its links as mur_id / qu_id; the "all matching filters" list as has_mur / has_qu
    const hasMur = !!(row.has_mur || row.mur_id);
    const hasQu = !!(row.has_qu || row.qu_id);
    const { co2, co2FromTotal } = co2For(row);
    // an existing MUR whose Co2 isn't the record's GWP-fossil (made before the fossil rule) — reported only
    const fossil = numOrNull(row.gwp_fossil_kg_co2e_quant);
    const murCo2 = numOrNull(row.mur_co2_value);
    const existingCo2Differs = hasMur && fossil !== null && murCo2 !== null && Math.abs(murCo2 - fossil) > 1e-6;

    if (hasMur && hasQu) {
        return { status: 'linked', label: 'Already linked', unit, quantityTypeName: row.mur_quantity_type || rule?.quantityTypeName, existingCo2Differs };
    }
    // the MUR exists: only its Product material is missing — whatever the record's own unit, the QU takes
    // the MUR's quantity type
    if (hasMur) {
        return {
            status: 'qu_only',
            label: 'QU only (MUR exists)',
            unit,
            quantityTypeName: row.mur_quantity_type || rule?.quantityTypeName,
            co2: murCo2 ?? co2,
            biogenic: numOrNull(row.mur_biogenic_value) ?? numOrNull(row.gwp_biogenic_kg_co2e_quant),
            weight: numOrNull(row.mur_weight_value),
            existingCo2Differs,
        };
    }
    if (!rule) {
        return {
            status: 'excluded',
            label: unit ? `Excluded — unit "${unit}" has no quantity type` : 'Excluded — no unit',
            unit,
        };
    }

    const weight = rule.weightField ? numOrNull(row[rule.weightField]) : 1;
    const zeroWeight = weight === null || weight === 0;

    if (!hasMur && zeroWeight && !includeZeroWeight) {
        return { status: 'excluded', label: 'Excluded — weight would be 0', unit, quantityTypeName: rule.quantityTypeName, weight };
    }

    return {
        status: hasQu ? 'mur_only' : 'both',
        label: hasQu ? 'MUR only (QU exists)' : 'MUR + QU',
        unit,
        quantityTypeName: rule.quantityTypeName,
        co2,
        co2FromTotal,
        biogenic: numOrNull(row.gwp_biogenic_kg_co2e_quant),
        weight,
        zeroWeight,
    };
};

const CHUNK = 200;

const BulkGenerateModal = ({
    onClose,
    onComplete,
    selectedRows,          // rows already on screen, may be empty
    appliedFilters,        // used for the "all matching filters" scope
    epdCategories,
    materialTypeGroups,    // grouped Nezo EPD categories from the page
}) => {
    const [scope, setScope] = useState(selectedRows.length ? 'selected' : 'filtered');
    const [candidates, setCandidates] = useState(null);   // filter-wide rows
    const [loadingCandidates, setLoadingCandidates] = useState(false);
    const [candidateError, setCandidateError] = useState(null);

    const [categoryId, setCategoryId] = useState('');
    const [createQu, setCreateQu] = useState(true);
    const [quCategoryId, setQuCategoryId] = useState('');
    const [quColorHex, setQuColorHex] = useState('#000000');
    const [includeZeroWeight, setIncludeZeroWeight] = useState(false);

    const [running, setRunning] = useState(false);
    const [progress, setProgress] = useState({ done: 0, total: 0 });
    const [outcome, setOutcome] = useState(null);
    const [confirming, setConfirming] = useState(false);

    const loadCandidates = useCallback(async () => {
        setLoadingCandidates(true);
        setCandidateError(null);
        try {
            const res = await api.get('/nabers-ref-ef/link-candidates', { params: appliedFilters });
            setCandidates(res.data.data || []);
            if (res.data.capped) {
                setCandidateError(`Showing the first ${res.data.cap} matching records — narrow the filters to cover the rest.`);
            }
        } catch (err) {
            setCandidateError('Could not load matching records: ' + (err.response?.data?.message || err.message));
            setCandidates([]);
        } finally {
            setLoadingCandidates(false);
        }
    }, [appliedFilters]);

    useEffect(() => {
        if (scope === 'filtered' && candidates === null && !loadingCandidates) loadCandidates();
    }, [scope, candidates, loadingCandidates, loadCandidates]);

    const sourceRows = scope === 'selected' ? selectedRows : (candidates || []);
    const planned = sourceRows.map((row) => ({ row, plan: planRow(row, includeZeroWeight) }));

    const counts = planned.reduce((acc, p) => {
        acc[p.plan.status] = (acc[p.plan.status] || 0) + 1;
        return acc;
    }, {});
    // a "QU only" row has nothing to do when Product materials aren't being created
    const actionable = planned.filter((p) => ['both', 'mur_only'].includes(p.plan.status)
        || (createQu && p.plan.status === 'qu_only'));
    const willCreateMur = planned.filter((p) => ['both', 'mur_only'].includes(p.plan.status)).length;
    const willCreateQu = createQu ? planned.filter((p) => ['both', 'qu_only'].includes(p.plan.status)).length : 0;
    const quForExistingMur = createQu ? (counts.qu_only || 0) : 0;
    const fromTotal = planned.filter((p) => p.plan.co2FromTotal && ['both', 'mur_only'].includes(p.plan.status)).length;
    const existingDiffer = planned.filter((p) => p.plan.existingCo2Differs).length;

    // each field is required only when something in the batch needs it
    const needMurCategory = willCreateMur > 0;
    const needMaterialType = willCreateQu > 0;
    const canRun = actionable.length > 0 && !running
        && (!needMurCategory || !!categoryId)
        && (!needMaterialType || !!quCategoryId);

    const run = async () => {
        setConfirming(false);
        setRunning(true);
        const ids = actionable.map((p) => p.row.Id);
        setProgress({ done: 0, total: ids.length });

        const all = [];
        try {
            for (let i = 0; i < ids.length; i += CHUNK) {
                const batch = ids.slice(i, i + CHUNK);
                const res = await api.post('/nabers-ref-ef/bulk-generate', {
                    efIds: batch,
                    categoryId: categoryId || undefined,
                    createQu,
                    quCategoryId: createQu ? quCategoryId : undefined,
                    quColorHex,
                    includeZeroWeight,
                });
                all.push(...(res.data.results || []));
                setProgress({ done: Math.min(i + CHUNK, ids.length), total: ids.length });
            }

            const summary = all.reduce((acc, r) => {
                acc[r.status] = (acc[r.status] || 0) + 1;
                return acc;
            }, {});
            setOutcome({ summary, results: all });
        } catch (err) {
            setOutcome({
                summary: {},
                results: all,
                fatal: err.response?.data?.message || err.message,
            });
        } finally {
            setRunning(false);
        }
    };

    const errorRows = outcome ? outcome.results.filter((r) => r.status === 'error') : [];
    const quOnlyDone = outcome
        ? outcome.results.filter((r) => (r.actions || []).includes('mur_exists') && (r.actions || []).includes('qu_created')).length
        : 0;
    const fellBackToTotal = outcome
        ? outcome.results.filter((r) => (r.actions || []).includes('mur_created')
            && (r.problems || []).includes('No GWP-Fossil — CO₂ uses GWP-Total'))
        : [];

    const copyErrors = () => {
        const text = errorRows.map((r) => `${r.efId}\t${(r.problems || []).join('; ')}`).join('\n');
        if (navigator.clipboard) navigator.clipboard.writeText(text);
    };

    // ── Result view ──────────────────────────────────────────────────────────
    if (outcome) {
        return (
            <div className="modal-overlay">
                <div className="modal-content bulk-modal">
                    <h2>Generation Results</h2>
                    {outcome.fatal && <div className="bulk-alert bulk-alert-error">Stopped early: {outcome.fatal}</div>}
                    <div className="bulk-stats">
                        <div className="bulk-stat bulk-stat-ok">
                            <span className="bulk-stat-num">{outcome.summary.created || 0}</span> created
                        </div>
                        <div className="bulk-stat">
                            <span className="bulk-stat-num">{outcome.summary.skipped || 0}</span> already linked
                        </div>
                        <div className="bulk-stat bulk-stat-warn">
                            <span className="bulk-stat-num">{outcome.summary.excluded || 0}</span> excluded
                        </div>
                        <div className="bulk-stat bulk-stat-error">
                            <span className="bulk-stat-num">{outcome.summary.error || 0}</span> failed
                        </div>
                    </div>

                    {quOnlyDone > 0 && (
                        <div className="bulk-alert bulk-alert-ok">
                            {quOnlyDone} Product material{quOnlyDone === 1 ? '' : 's'} created for MURs that already existed (no new MURs).
                        </div>
                    )}
                    {fellBackToTotal.length > 0 && (
                        <div className="bulk-alert bulk-alert-warn">
                            {fellBackToTotal.length} new MUR{fellBackToTotal.length === 1 ? '' : 's'} had no GWP-Fossil, so CO₂ used GWP-Total:{' '}
                            {fellBackToTotal.slice(0, 10).map((r) => r.efId).join(', ')}{fellBackToTotal.length > 10 ? '…' : ''}
                        </div>
                    )}
                    {existingDiffer > 0 && (
                        <div className="bulk-alert bulk-alert-warn">
                            {existingDiffer} existing MUR{existingDiffer === 1 ? "'s" : "s'"} CO₂ in this batch doesn't match the record's GWP-Fossil
                            (made before the fossil rule). They weren't changed.
                        </div>
                    )}

                    {errorRows.length > 0 && (
                        <div className="bulk-errors">
                            <div className="bulk-errors-head">
                                <strong>Failures</strong>
                                <button type="button" className="btn-clear" onClick={copyErrors}>Copy all</button>
                            </div>
                            <ul>
                                {errorRows.slice(0, 50).map((r) => (
                                    <li key={r.efId}>
                                        <code>{r.efId}</code> — {(r.problems || []).join('; ')}
                                    </li>
                                ))}
                            </ul>
                            {errorRows.length > 50 && <p>…and {errorRows.length - 50} more (use Copy all).</p>}
                        </div>
                    )}

                    <div className="form-actions">
                        <button type="button" className="btn-save" onClick={() => { onComplete(); }}>
                            Done
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // ── Setup view ───────────────────────────────────────────────────────────
    return (
        <div className="modal-overlay">
            <div className="modal-content bulk-modal">
                <h2>Generate Missing MURs &amp; Materials</h2>

                <div className="bulk-scope">
                    <label className={scope === 'selected' ? 'bulk-scope-opt active' : 'bulk-scope-opt'}>
                        <input
                            type="radio"
                            checked={scope === 'selected'}
                            disabled={!selectedRows.length || running}
                            onChange={() => setScope('selected')}
                        />
                        Selected rows ({selectedRows.length})
                    </label>
                    <label className={scope === 'filtered' ? 'bulk-scope-opt active' : 'bulk-scope-opt'}>
                        <input
                            type="radio"
                            checked={scope === 'filtered'}
                            disabled={running}
                            onChange={() => setScope('filtered')}
                        />
                        All rows matching the current filters
                        {loadingCandidates ? ' (loading…)' : candidates ? ` (${candidates.length})` : ''}
                    </label>
                </div>

                {candidateError && <div className="bulk-alert bulk-alert-warn">{candidateError}</div>}

                <div className="bulk-inputs">
                    <div className="form-group">
                        <label>EPD Category for the MUR: {needMurCategory && <span className="req">*</span>}</label>
                        <select
                            value={categoryId}
                            onChange={(e) => setCategoryId(e.target.value)}
                            disabled={running || !needMurCategory}
                            aria-label="EPD Category for the MUR"
                        >
                            <option value="">{needMurCategory ? '— Select category —' : '— Not needed —'}</option>
                            {epdCategories.map((c) => (
                                <option key={c.Id} value={c.Id}>{c.Categories}</option>
                            ))}
                        </select>
                        <small>
                            {needMurCategory
                                ? 'Applied to every new MUR in this batch — scope the batch by Material Type first.'
                                : 'Not needed — every record here already has a MUR.'}
                        </small>
                    </div>

                    <div className="form-group">
                        <label className="bulk-check">
                            <input
                                type="checkbox"
                                checked={createQu}
                                onChange={(e) => setCreateQu(e.target.checked)}
                                disabled={running}
                            />
                            Also create Product materials (QUs)
                        </label>
                    </div>

                    {createQu && (
                        <>
                            <div className="form-group">
                                <label>Material Type (Nezo EPD): {needMaterialType && <span className="req">*</span>}</label>
                                <select value={quCategoryId} onChange={(e) => setQuCategoryId(e.target.value)} disabled={running} aria-label="Material Type (Nezo EPD)">
                                    <option value="">— Select material type —</option>
                                    {materialTypeGroups.map((node) => (
                                        node.type === 'group' ? (
                                            <optgroup key={node.id} label={node.label}>
                                                {node.options.map((o) => (
                                                    <option key={o.id} value={o.id}>{o.label}</option>
                                                ))}
                                            </optgroup>
                                        ) : (
                                            <option key={node.id} value={node.id}>{node.label}</option>
                                        )
                                    ))}
                                </select>
                            </div>
                            <div className="form-group">
                                <label>Colour:</label>
                                <input type="color" value={quColorHex} onChange={(e) => setQuColorHex(e.target.value)} disabled={running} />
                            </div>
                        </>
                    )}

                    <div className="form-group">
                        <label className="bulk-check">
                            <input
                                type="checkbox"
                                checked={includeZeroWeight}
                                onChange={(e) => setIncludeZeroWeight(e.target.checked)}
                                disabled={running}
                            />
                            Include rows whose weight would be 0
                        </label>
                        <small>Off by default — those records have no density for their unit.</small>
                    </div>
                </div>

                <div className="bulk-stats">
                    <div className="bulk-stat bulk-stat-ok">
                        <span className="bulk-stat-num">{willCreateMur}</span> MURs to create
                    </div>
                    <div className="bulk-stat bulk-stat-ok">
                        <span className="bulk-stat-num">{willCreateQu}</span> materials to create
                    </div>
                    <div className="bulk-stat">
                        <span className="bulk-stat-num">{counts.linked || 0}</span> already linked
                    </div>
                    <div className="bulk-stat bulk-stat-warn">
                        <span className="bulk-stat-num">{counts.excluded || 0}</span> excluded
                    </div>
                </div>

                <div className="bulk-preview">
                    <table>
                        <thead>
                            <tr>
                                <th>Name</th><th>Unit</th><th>Quantity Type</th>
                                <th>Co2</th><th>Biogenic</th><th>Weight</th><th>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {planned.slice(0, 25).map(({ row, plan }) => (
                                <tr key={row.Id} className={`plan-${plan.status}`}>
                                    <td title={row.name}>{row.name || row.Id}</td>
                                    <td>{plan.unit || '-'}</td>
                                    <td>{plan.quantityTypeName || '-'}</td>
                                    <td title={plan.co2FromTotal ? 'No GWP-Fossil on this record — CO₂ uses GWP-Total' : undefined}>
                                        {plan.co2 ?? '-'}
                                        {plan.co2FromTotal && <span className="co2-from-total"> (total)</span>}
                                    </td>
                                    <td>{plan.biogenic ?? '-'}</td>
                                    <td className={plan.zeroWeight ? 'weight-zero' : ''}>{plan.weight ?? '-'}</td>
                                    <td>{plan.label}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {planned.length > 25 && <p className="bulk-more">…and {planned.length - 25} more rows.</p>}
                    {planned.length === 0 && <p className="bulk-more">No rows in scope.</p>}
                </div>

                {(fromTotal > 0 || existingDiffer > 0) && (
                    <div className="bulk-alert bulk-alert-warn">
                        {fromTotal > 0 && <div>{fromTotal} record{fromTotal === 1 ? '' : 's'} without GWP-Fossil will use GWP-Total for CO₂ (marked “total”).</div>}
                        {existingDiffer > 0 && <div>{existingDiffer} existing MUR{existingDiffer === 1 ? "'s" : "s'"} CO₂ doesn't match GWP-Fossil — left as is.</div>}
                    </div>
                )}

                {running && (
                    <div className="bulk-progress">
                        <div className="bulk-progress-bar">
                            <div
                                className="bulk-progress-fill"
                                style={{ width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%` }}
                            />
                        </div>
                        <span>{progress.done} / {progress.total}</span>
                    </div>
                )}

                <div className="form-actions">
                    <button type="button" className="btn-save" disabled={!canRun} onClick={() => setConfirming(true)}>
                        {running ? 'Generating…' : `Generate for ${actionable.length} record(s)`}
                    </button>
                    <button type="button" className="btn-cancel" onClick={onClose} disabled={running}>
                        Cancel
                    </button>
                </div>

                {confirming && (
                    <div className="notification-modal-overlay" onClick={() => setConfirming(false)}>
                        <div className="notification-modal-content" onClick={(e) => e.stopPropagation()}>
                            <h2 className="notification-title">Confirm</h2>
                            <p className="notification-message">
                                This will create <strong>{willCreateMur}</strong> MUR record(s)
                                {createQu ? <> and <strong>{willCreateQu}</strong> Product material(s)</> : null}
                                {quForExistingMur > 0 ? <> — {quForExistingMur} of them for MURs that already exist</> : null}.
                                Existing MURs and materials are never modified.
                            </p>
                            <div className="form-actions">
                                <button type="button" className="btn-save" onClick={run}>Generate</button>
                                <button type="button" className="btn-cancel" onClick={() => setConfirming(false)}>Back</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BulkGenerateModal;
